import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaPen,
  FaRegClock,
} from "react-icons/fa";
import { fetchBarExams, fetchMyBarExamSubmission } from "../api/barExam";
import { hasAuthenticatedSession } from "../api";

import PublicContent from "../content/PublicContent";

const MONTH_FILTERS = [
  { month: 8, label: "8월 모의고사" },
  { month: 6, label: "6월 모의고사" },
];

const getSubjectName = (subject) => {
  if (typeof subject === "string") {
    return subject;
  }

  return subject.title || subject.name || subject.subject_name;
};

const getExamMonth = (exam) => {
  if (!exam) return null;

  const text = [exam.title, exam.name, exam.exam_round]
    .filter(Boolean)
    .join(" ");
  const monthMatch = text.match(/(\d{1,2})\s*월/);
  const shortMatch = text.match(/(\d{1,2})\s*모/);

  if (monthMatch) {
    return Number(monthMatch[1]);
  }

  if (shortMatch) {
    return Number(shortMatch[1]);
  }

  const round = Number(exam.exam_round);
  return Number.isNaN(round) ? null : round;
};

const getExamSortScore = (exam) => {
  const year = Number(exam.exam_year) || 0;
  const month = getExamMonth(exam) || 0;

  return year * 100 + month;
};

const getExamPeriodLabel = (exam) => {
  const month = getExamMonth(exam);
  const parts = [];

  if (exam.exam_year) {
    parts.push(`${exam.exam_year}년`);
  }

  if (month) {
    parts.push(`${month}월`);
  } else if (exam.exam_round) {
    parts.push(`${exam.exam_round}회`);
  }

  return parts.join(" ") || "모의고사";
};

const BarExamList = ({ isHome = false }) => {
  const [exams, setExams] = useState([]);
  const [examSubmissions, setExamSubmissions] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(8);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadExams = async () => {
      try {
        const [data, authenticated] = await Promise.all([
          fetchBarExams(),
          hasAuthenticatedSession(),
        ]);
        const examList = Array.isArray(data) ? data : data.results || [];
        const sortedExams = [...examList].sort(
          (a, b) => getExamSortScore(b) - getExamSortScore(a),
        );
        const submissionEntries = authenticated
          ? await Promise.all(
              sortedExams.map(async (exam) => {
                try {
                  const submission = await fetchMyBarExamSubmission(exam.id);
                  return [exam.id, submission];
                } catch (error) {
                  console.error(
                    `Error fetching submission for exam ${exam.id}:`,
                    error,
                  );
                  return [exam.id, null];
                }
              }),
            )
          : sortedExams.map((exam) => [
              exam.id,
              { exists: false, submission: null },
            ]);

        const hasAugustExam = sortedExams.some(
          (exam) => getExamMonth(exam) === 8,
        );
        const hasJuneExam = sortedExams.some((exam) => getExamMonth(exam) === 6);

        setExams(sortedExams);
        setIsAuthenticated(authenticated);
        setExamSubmissions(Object.fromEntries(submissionEntries));
        setSelectedMonth(
          hasAugustExam ? 8 : hasJuneExam ? 6 : getExamMonth(sortedExams[0]) || 8,
        );
      } catch (error) {
        console.error("Error fetching bar exams:", error);
        setError("시험 목록을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };

    loadExams();
  }, []);

  const examsByMonth = useMemo(
    () =>
      exams.reduce((groups, exam) => {
        const month = getExamMonth(exam);
        if (!month) return groups;

        return {
          ...groups,
          [month]: [...(groups[month] || []), exam],
        };
      }, {}),
    [exams],
  );

  const selectedMonthExams = useMemo(
    () => exams.filter((exam) => getExamMonth(exam) === selectedMonth),
    [exams, selectedMonth],
  );

  const featuredExam = selectedMonthExams[0] || exams[0];
  const featuredSubmission = featuredExam
    ? examSubmissions[featuredExam.id]
    : null;
  const hasFeaturedSubmission = Boolean(featuredSubmission?.exists);
  const canEditFeatured = Boolean(
    hasFeaturedSubmission && featuredSubmission.submission?.can_edit,
  );
  const selectedMonthLabel =
    MONTH_FILTERS.find((filter) => filter.month === selectedMonth)?.label ||
    `${selectedMonth}월 모의고사`;

  const handlePrimaryAction = (exam, submissionStatus) => {
    const hasSubmitted = Boolean(submissionStatus?.exists);
    const canEdit = Boolean(
      hasSubmitted && submissionStatus.submission?.can_edit,
    );

    if (hasSubmitted && !canEdit) {
      navigate("/bar-exam/result", {
        state: { result: submissionStatus, examId: exam.id },
      });
      return;
    }

    navigate(`/bar-exam/${exam.id}/answer`);
  };

  const getPrimaryActionLabel = (submissionStatus) => {
    const hasSubmitted = Boolean(submissionStatus?.exists);
    const canEdit = Boolean(
      hasSubmitted && submissionStatus.submission?.can_edit,
    );

    if (submissionStatus === null) return "상태 확인 실패";
    if (!hasSubmitted) return "답안 입력";
    if (canEdit) return "답안 수정하기";
    return "내 결과 보기";
  };

  return (
    <Flex
      as="main"
      direction="column"
      align="center"
      minH="100vh"
      bg="gray.50"
      p={{ base: 4, md: 8 }}
    >
      <Box width="full" maxW="1120px">
        <Flex justify="flex-end" gap="2" pt="2" wrap="wrap">
          <Button as={RouterLink} to="/" variant="ghost">홈</Button>
          <Button as={RouterLink} to="/guide" variant="ghost">이용 가이드</Button>
          <Button
            variant="ghost"
            onClick={() => navigate(isAuthenticated ? "/answers" : "/answers/new")}
          >
            {isAuthenticated ? "내 답안지" : "주관식 답안 연습"}
          </Button>
          {!isAuthenticated && (
            <Button
              colorScheme="blue"
              variant="outline"
              onClick={() =>
                navigate("/login", {
                  state: { returnTo: "/bar-exam" },
                })
              }
            >
              로그인
            </Button>
          )}
        </Flex>
        <Box as="section" py={{ base: 6, md: 9 }}>
          <Badge colorScheme="blue" px="3" py="1" rounded="md" mb="4">
            객관식 풀서비스
          </Badge>
          <Flex
            align={{ base: "start", md: "end" }}
            justify="space-between"
            gap="5"
            direction={{ base: "column", md: "row" }}
          >
            <Box maxW="680px">
              <Heading as="h1" size="xl" lineHeight="1.25" mb="3">
                변호사시험 모의고사 성적을 바로 확인하세요
              </Heading>
              <Text color="gray.600" fontSize={{ base: "md", md: "lg" }}>
                답안 입력부터 예상 등수, 과목별 점수, 전체 통계까지 한 화면에서
                이어집니다.
              </Text>
            </Box>
            <HStack spacing="2" flexWrap="wrap">
              {MONTH_FILTERS.map((filter) => {
                const isActive = selectedMonth === filter.month;
                const count = examsByMonth[filter.month]?.length || 0;

                return (
                  <Button
                    key={filter.month}
                    leftIcon={
                      <Icon
                        as={filter.month === 8 ? FaRegClock : FaClipboardList}
                      />
                    }
                    colorScheme={isActive ? "blue" : "gray"}
                    variant={isActive ? "solid" : "outline"}
                    isDisabled={loading}
                    onClick={() => setSelectedMonth(filter.month)}
                  >
                    {filter.label}
                    {count > 1 ? ` ${count}` : ""}
                  </Button>
                );
              })}
            </HStack>
          </Flex>
        </Box>

        {loading && (
          <Flex align="center" justify="center" minH="360px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        )}

        {error && (
          <Alert status="error" rounded="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {!loading && !error && exams.length === 0 && (
          <Box p="6" rounded="md" shadow="sm" width="full" bg="white">
            <Text textAlign="center" color="gray.500">
              등록된 시험이 없습니다.
            </Text>
          </Box>
        )}

        {!loading && !error && exams.length > 0 && (
          <>
            {featuredExam && (
              <Box
                as="section"
                bg="white"
                borderWidth="1px"
                rounded="md"
                shadow="sm"
                p={{ base: 5, md: 7 }}
                mb="6"
              >
                <Flex
                  align={{ base: "start", lg: "center" }}
                  justify="space-between"
                  gap="5"
                  direction={{ base: "column", lg: "row" }}
                >
                  <Box flex="1">
                    <HStack spacing="2" mb="3" flexWrap="wrap">
                      <Badge colorScheme="blue" px="2" py="1" rounded="md">
                        {selectedMonthLabel}
                      </Badge>
                      {hasFeaturedSubmission && (
                        <Badge
                          colorScheme={canEditFeatured ? "orange" : "green"}
                          px="2"
                          py="1"
                          rounded="md"
                        >
                          {canEditFeatured ? "수정 가능" : "제출 완료"}
                        </Badge>
                      )}
                    </HStack>
                    <Heading as="h2" size="lg" mb="2">
                      {featuredExam.title}
                    </Heading>
                    <Text color="gray.600" mb="4">
                      {getExamPeriodLabel(featuredExam)} 풀서비스
                    </Text>
                    <Wrap>
                      {featuredExam.subjects?.length > 0 ? (
                        featuredExam.subjects.map((subject, index) => (
                          <WrapItem
                            key={
                              subject.id || `${getSubjectName(subject)}-${index}`
                            }
                          >
                            <Badge colorScheme="teal" px="2" py="1" rounded="md">
                              {getSubjectName(subject)}
                            </Badge>
                          </WrapItem>
                        ))
                      ) : (
                        <Text color="gray.500" fontSize="sm">
                          과목 정보가 없습니다.
                        </Text>
                      )}
                    </Wrap>
                  </Box>

                  <SimpleGrid
                    columns={{ base: 1, sm: 3 }}
                    spacing="3"
                    width={{ base: "full", lg: "460px" }}
                  >
                    <Box borderWidth="1px" rounded="md" p="4">
                      <Icon as={FaPen} color="blue.500" mb="2" />
                      <Text fontWeight="bold">답안 입력</Text>
                      <Text color="gray.500" fontSize="sm">
                        객관식 번호를 빠르게 입력
                      </Text>
                    </Box>
                    <Box borderWidth="1px" rounded="md" p="4">
                      <Icon as={FaCheckCircle} color="green.500" mb="2" />
                      <Text fontWeight="bold">즉시 채점</Text>
                      <Text color="gray.500" fontSize="sm">
                        점수와 정오 현황 확인
                      </Text>
                    </Box>
                    <Box borderWidth="1px" rounded="md" p="4">
                      <Icon as={FaChartLine} color="purple.500" mb="2" />
                      <Text fontWeight="bold">전체 통계</Text>
                      <Text color="gray.500" fontSize="sm">
                        등수와 취약 문항 분석
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Flex>

                <Divider my="5" />

                <Flex gap="3" direction={{ base: "column", sm: "row" }}>
                  <Button
                    colorScheme={canEditFeatured ? "orange" : "blue"}
                    size="lg"
                    leftIcon={<FaPen />}
                    isDisabled={featuredSubmission === null}
                    onClick={() =>
                      handlePrimaryAction(featuredExam, featuredSubmission)
                    }
                  >
                    {getPrimaryActionLabel(featuredSubmission)}
                  </Button>
                  <Button
                    colorScheme="teal"
                    variant="outline"
                    size="lg"
                    leftIcon={<FaChartLine />}
                    as={RouterLink}
                    to={`/bar-exam/${featuredExam.id}/statistics`}
                  >
                    전체 통계 보기
                  </Button>
                </Flex>
              </Box>
            )}

            <Flex align="center" justify="space-between" gap="3" mb="4">
              <Heading as="h2" size="md">
                이용 가능한 모의고사
              </Heading>
              <Text color="gray.500" fontSize="sm">
                {exams.length}개 시험
              </Text>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {exams.map((exam) => {
                const submissionStatus = examSubmissions[exam.id];
                const hasSubmitted = Boolean(submissionStatus?.exists);
                const canEdit = Boolean(
                  hasSubmitted && submissionStatus.submission?.can_edit,
                );
                const statusUnavailable = submissionStatus === null;

                return (
                  <Box
                    key={exam.id}
                    p="5"
                    rounded="md"
                    borderWidth="1px"
                    shadow="sm"
                    bg="white"
                  >
                    <Flex justify="space-between" align="start" gap="3" mb="3">
                      <Box>
                        <Heading as="h3" size="sm" mb="1">
                          {exam.title}
                        </Heading>
                        <Text color="gray.500" fontSize="sm">
                          {getExamPeriodLabel(exam)}
                        </Text>
                      </Box>
                      {hasSubmitted && (
                        <Badge
                          colorScheme={canEdit ? "orange" : "green"}
                          whiteSpace="nowrap"
                        >
                          {canEdit ? "수정 가능" : "제출 완료"}
                        </Badge>
                      )}
                    </Flex>

                    <Wrap mb="5">
                      {exam.subjects?.length > 0 ? (
                        exam.subjects.map((subject, index) => (
                          <WrapItem
                            key={
                              subject.id || `${getSubjectName(subject)}-${index}`
                            }
                          >
                            <Badge colorScheme="blue" px="2" py="1" rounded="md">
                              {getSubjectName(subject)}
                            </Badge>
                          </WrapItem>
                        ))
                      ) : (
                        <Text color="gray.500" fontSize="sm">
                          과목 정보가 없습니다.
                        </Text>
                      )}
                    </Wrap>

                    <Flex gap="3" direction={{ base: "column", sm: "row" }}>
                      <Button
                        colorScheme={canEdit ? "orange" : "blue"}
                        width="full"
                        leftIcon={<FaPen />}
                        isDisabled={statusUnavailable}
                        onClick={() => handlePrimaryAction(exam, submissionStatus)}
                      >
                        {getPrimaryActionLabel(submissionStatus)}
                      </Button>
                      <Button
                        colorScheme="teal"
                        variant="outline"
                        width="full"
                        leftIcon={<FaChartLine />}
                        as={RouterLink}
                        to={`/bar-exam/${exam.id}/statistics`}
                      >
                        전체 통계
                      </Button>
                    </Flex>
                  </Box>
                );
              })}
            </SimpleGrid>
          </>
        )}
        {isHome && <PublicContent page="home" />}
      </Box>
    </Flex>
  );
};

export default BarExamList;
